import { FC } from "react";
import SingleSelectDropdown from "@app/shared/checkbox-dropdown/SingleSelectDropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import DualRangeSlider from "@app/shared/DualRangeSlider";
import { Toggle } from "@app/shared/Toggle";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ZipCoverageMapMembershipType, ZipCoverageResultsAsOfOption } from "@app/projects/zip-projects/maps/interfaces";
import { HUNDRED_PERCENT_FILTER, ZERO_PERCENT_FILTER } from "@app/projects/zip-projects/maps/ZipCoverageMapConstants";
import { ZipCoverageMapMembershipTypeFilter } from "@app/projects/zip-projects/maps/ZipCoverageMapMembershipTypeFilter";

interface ZipCoverageMapFilterPanelProps {
  resultsAsOfOptions: ZipCoverageResultsAsOfOption[];
  selectedResultsAsOfId: number | null;
  onSelectResultsAsOf: (id: number) => void;

  showMembershipTypeFilter: boolean;
  membershipType: ZipCoverageMapMembershipType;
  onSelectMembershipType: (value: ZipCoverageMapMembershipType) => void;
  primaryMembershipAlias?: string;
  secondaryMembershipAlias?: string;

  serviceAreaItems: NestingParentDropdownItem[];
  serviceAreaLoading: boolean;
  selectedServiceAreaKeys: string[];
  selectedServiceAreaLabel: string;
  onSelectServiceAreas: (keys: string[]) => void;
  allowMultipleServiceAreas?: boolean;
  showZeroMembership: boolean;
  onShowZeroMembershipChange: (showZeroMembership: boolean) => void;

  specialtyOptions: DropdownItem<string>[];
  selectedSpecialty: DropdownItem<string>;
  onSelectSpecialty: (specialty: DropdownItem<string>) => void;

  providerSourceItems: NestingParentDropdownItem[];
  selectedProviderSources: ProviderNetworkTypeEnum[];
  onSelectProviderSources: (sources: ProviderNetworkTypeEnum[]) => void;

  servesRange: [number, number];
  onServesRangeChange: (range: [number, number]) => void;
  gainsRange: [number, number];
  onGainsRangeChange: (range: [number, number]) => void;
  impactRange: [number, number];
  onImpactRangeChange: (range: [number, number]) => void;

  createDisabled: boolean;
  clearAllDisabled: boolean;
  onCreateMap: () => void;
  onClearAll: () => void;
  isLoading: boolean;
}

/**
 * ZIP map setup panel: Results As Of / Service Area / Specialty / Provider Source / Advanced Filters
 * + Create / Clear All. Prop-driven (route owns state). Styling: _zip-coverage-map.scss.
 * Mirrors county's FilterControl.tsx. ZIP deviations: published time slices only; Service Area
 * drills to a single zip; no county-status gate; distinct Create/Clear conditions; no PCI.
 */
export const ZipCoverageMapFilterPanel: FC<ZipCoverageMapFilterPanelProps> = ({
  resultsAsOfOptions,
  selectedResultsAsOfId,
  onSelectResultsAsOf,
  showMembershipTypeFilter,
  membershipType,
  onSelectMembershipType,
  primaryMembershipAlias,
  secondaryMembershipAlias,
  serviceAreaItems,
  serviceAreaLoading,
  selectedServiceAreaKeys,
  selectedServiceAreaLabel,
  onSelectServiceAreas,
  allowMultipleServiceAreas = false,
  showZeroMembership,
  onShowZeroMembershipChange,
  specialtyOptions,
  selectedSpecialty,
  onSelectSpecialty,
  providerSourceItems,
  selectedProviderSources,
  onSelectProviderSources,
  servesRange,
  onServesRangeChange,
  gainsRange,
  onGainsRangeChange,
  impactRange,
  onImpactRangeChange,
  createDisabled,
  clearAllDisabled,
  onCreateMap,
  onClearAll,
  isLoading,
}) => (
  <div className="zip-coverage-map__filter-container">
    <div className="mb-2">
      <QESSelect
        allowEmpty={false}
        containerClassName="w-100"
        isValid={true}
        label="Results As Of"
        testId="zip-coverage-map__filter--results-as-of"
        value={selectedResultsAsOfId ?? ""}
        onChange={(e) => onSelectResultsAsOf(Number(e.target.value))}
      >
        {resultsAsOfOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </QESSelect>
    </div>
    {showMembershipTypeFilter && (
      <div className="mb-2">
        <ZipCoverageMapMembershipTypeFilter
          membershipType={membershipType}
          setMembershipType={onSelectMembershipType}
          primaryMembershipAlias={primaryMembershipAlias}
          secondaryMembershipAlias={secondaryMembershipAlias}
        />
      </div>
    )}
    <div className="mb-2">
      {/* The toggle sits on the label's row but outside the dropdown's own <label htmlFor> — nested there, a click
          on it would forward to the input and open the dropdown. */}
      <div className="zip-coverage-map__service-area-header">
        {/* aria-hidden: the dropdown keeps its own (visually hidden) label, which is what names the input. */}
        <span className="zip-coverage-map__service-area-label" aria-hidden="true">Service Area</span>
        <Toggle
          active={showZeroMembership}
          label="Show Zero Membership"
          labelSide="left"
          testId="zip-coverage-map__filter--show-zero-membership"
          onChange={() => onShowZeroMembershipChange(!showZeroMembership)}
        />
      </div>
      <MultiselectNestedDropdown
        customSelectInputText={serviceAreaLoading
          ? "Loading…"
          : (!allowMultipleServiceAreas || selectedServiceAreaKeys.length === 1 ? selectedServiceAreaLabel : undefined)}
        disabled={serviceAreaLoading}
        items={serviceAreaItems}
        containerClassName="w-100"
        hideSelectAll={!allowMultipleServiceAreas}
        hideParentCheckboxes={!allowMultipleServiceAreas}
        hideGrandparentExpanderIcon
        hideLabel
        alwaysGroupByGrandparentTitle
        label="Service Area"
        selectedItems={selectedServiceAreaKeys}
        testId="zip-coverage-map__filter--service-area"
        onSelectionChanged={(selectedItems) =>
          onSelectServiceAreas(allowMultipleServiceAreas ? selectedItems : selectedItems.slice(-1))
        }
      />
    </div>
    <div className="mb-2">
      <SingleSelectDropdown
        label="Specialty"
        containerClassName="w-100 zip-coverage-map__filter-specialty"
        items={specialtyOptions}
        selectedItem={selectedSpecialty}
        testId="zip-coverage-map__filter--specialty"
        onSelectionChanged={onSelectSpecialty}
      />
    </div>
    <div className="mb-2">
      <MultiselectNestedDropdown
        items={providerSourceItems}
        containerClassName="w-100"
        label="Provider Source"
        hideParentCheckboxes={false}
        parentsAsValues
        keepParentOpen
        hideSelectAll
        hideSearch
        selectedItems={selectedProviderSources}
        testId="zip-coverage-map__filter--provider-source"
        onSelectionChanged={(selectedItems) =>
          onSelectProviderSources(selectedItems.map((item) => item as ProviderNetworkTypeEnum))
        }
      />
    </div>
    <div className="mb-3">
      <AccordionContainer className="mt-3">
        <Accordion
          className="zip-coverage-map__advanced-filters-accordion"
          title="Advanced Filters"
          defaultOpen={false}
          testId="zip-coverage-map__filter--advanced-filters-accordion"
        >
          <AccordionContainer>
            <Accordion
              className="zip-coverage-map__advanced-filters-sub-accordion"
              title="Provider Contribution Measures"
              defaultOpen={true}
              testId="zip-coverage-map__filter--provider-contribution-measures-accordion"
            >
              <DualRangeSlider
                label="Serves %"
                minValue={ZERO_PERCENT_FILTER}
                maxValue={HUNDRED_PERCENT_FILTER}
                testId="zip-coverage-map__filter--serves-percent-slider"
                value={servesRange}
                onChange={onServesRangeChange}
              />
              <hr />
              <DualRangeSlider
                label="Gains %"
                minValue={ZERO_PERCENT_FILTER}
                maxValue={HUNDRED_PERCENT_FILTER}
                testId="zip-coverage-map__filter--gains-percent-slider"
                value={gainsRange}
                onChange={onGainsRangeChange}
              />
              <hr />
              <DualRangeSlider
                label="Impact %"
                minValue={ZERO_PERCENT_FILTER}
                maxValue={HUNDRED_PERCENT_FILTER}
                testId="zip-coverage-map__filter--impact-percent-slider"
                value={impactRange}
                onChange={onImpactRangeChange}
              />
            </Accordion>
          </AccordionContainer>
        </Accordion>
      </AccordionContainer>
    </div>
    <div className="d-flex justify-content-end">
      <QESButton
        className="me-2"
        qesType="secondary"
        disabled={createDisabled}
        onClick={onCreateMap}
        qesTestId="zip-coverage-map__create"
      >
        {isLoading ? "Loading…" : "Create Map"}
      </QESButton>
      <QESButton
        qesType="secondary"
        disabled={clearAllDisabled}
        onClick={onClearAll}
        qesTestId="zip-coverage-map__clear-all"
      >
        Clear All
      </QESButton>
    </div>
  </div>
);

export default ZipCoverageMapFilterPanel;
